import {TailwindContents} from '@wandb/weave/components/Tailwind';
import React, {useMemo, useState} from 'react';
import {twMerge} from 'tailwind-merge';

import type * as PanelBankSectionConfigTypes from '../../../state/views/panelBankSectionConfig/types';
import {getPanelSpec} from '../../../util/panelTypes';
import {PanelDragHandle} from '../../Panel/common/PanelDragHandle';
import {PanelViewVegaSpec} from '../../Panel/common/PanelViewVegaSpec';
import {ReportFullScreenPanelButton} from '../../Panel/FullScreenPanel/PanelFullScreenButton';
import {usePanelContext} from '../../Panel/PanelContextProvider';
import {PanelAddComment} from './PanelAddComment';
import {PanelViewComments} from './PanelViewComments';
import {ReportPanelEditButton} from './ReportPanelEditButton';
import {ReportPanelOverflowMenu} from './ReportPanelOverflowMenu';

export const ReportPanelHeader = ({
  panelBankSectionConfigRef,
}: {
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref;
}) => {
  const {isReadOnly, panelRef, panel} = usePanelContext();

  const panelType = panel.viewType;
  const panelSpec = useMemo(() => getPanelSpec(panelType), [panelType]);

  const [isOverflowMenuActive, setIsOverflowMenuActive] = useState(false);

  return (
    <TailwindContents>
      <div
        className={twMerge(
          'flex shrink-0',
          'mb-4 min-h-[24px]',
          // on a regular workspace OR report where multiple panels are visible, we want to only show
          // the actionable left side items when the mouse is over the panel. it should always be visible in full screen where we only
          // show 1 panel
          'opacity-0 focus-within:opacity-100 hover:opacity-100',
          // since we hide the actionable items until mouse hover on a regular workspace or report where we display multiple panels,
          // we still want to show the actionable items when overflow menu is active
          isOverflowMenuActive && 'opacity-100'
        )}
        data-test="panel-header">
        {!isReadOnly && <PanelDragHandle panelRef={panelRef} />}
        <div
          className={twMerge(
            // on a regular workspace OR report where multiple panels are visible, the actions should be rendered on top
            // of the drag handle and there's no back button to consider
            'absolute right-8'
          )}>
          <div className={twMerge('flex')}>
            <PanelViewVegaSpec panelType={panelType} />
            <ReportFullScreenPanelButton />
            <ReportPanelEditButton />
            <ReportPanelOverflowMenu
              panelBankSectionConfigRef={panelBankSectionConfigRef}
              panelSpec={panelSpec}
              isMenuOpen={isOverflowMenuActive}
              setIsMenuOpen={setIsOverflowMenuActive}
            />
            <PanelAddComment />
            <PanelViewComments />
          </div>
        </div>
      </div>
    </TailwindContents>
  );
};
